import { http, download } from './request'
import { BUILT_IN_SUPER_ADMIN_ACCOUNT } from '@/config/superAdmin'

const BIZ_PREFIX = '/biz/overseas/sysuser'
const SYS_USER_PREFIX = '/sys/user'
const SYS_USER_CENTER_PREFIX = '/sys/userCenter'

/** 后台政府账号，与后端 SystemUserCreateParam.registerType 一致 */
export const REGISTER_TYPE_BACKOFFICE = 100

/** C 端个人用户，与后端 registerType=200 / userMode=200 一致 */
export const REGISTER_TYPE_PERSONAL = 200

/** C 端企业用户，与后端 userMode=300 一致 */
export const REGISTER_TYPE_ENTERPRISE = 300

export { BUILT_IN_SUPER_ADMIN_ACCOUNT } from '@/config/superAdmin'

export type UserStatus = 'enabled' | 'disabled'

export interface UserRecord {
  id: string
  username: string
  realName: string
  phone: string
  /** 详情默认展示的 ICSP 密评密文；用户管理手机号明文不解此列 */
  phoneEnc?: string
  email: string
  /** 角色展示名，多个逗号分隔 */
  role: string
  department: string
  deptId?: string
  status: UserStatus
  createdAt: string
  lastLoginAt: string
  /** 编辑回显：注册类型 */
  registerType?: number
  /** 编辑回显：已授权角色 id（提交编辑时必须带回，避免被清空） */
  roleId?: string[]
  /** 与后端 SysUser.userMode 一致：100 后台人员，200 C端用户，300 C端企业用户 */
  userMode?: number
  /** 微信小程序 openId（详情接口；已绑定才有） */
  openId?: string
  /** 是否已绑定微信 openId（详情接口） */
  wxBound?: boolean
  hasPhone?: boolean
  /** 角色绑定完整性 */
  roleIntegrityStatus?: string
  roleIntegrityStatusName?: string
  /** 所绑角色菜单完整性汇总 */
  menuIntegrityStatus?: string
  menuIntegrityStatusName?: string
  /** 手机密评密文 / 密码密评密文完整性 */
  confIntegrityStatus?: string
  confIntegrityStatusName?: string
}

export function formatAclIntegrity(status: unknown, statusName?: unknown): string {
  const name = String(statusName ?? '').trim()
  if (name) return name
  const u = String(status ?? '').toUpperCase()
  if (u === 'PASSED') return '完整'
  if (u === 'TAMPERED') return '已篡改'
  if (u === 'UNSIGNED') return '未签名'
  if (u === 'DISABLED') return '未启用'
  return String(status ?? '').trim()
}

export function aclIntegrityPillClass(status?: string): string {
  const u = String(status ?? '').toUpperCase()
  if (u === 'PASSED') return 'status-pill--passed'
  if (u === 'TAMPERED') return 'status-pill--tampered'
  return 'status-pill--muted'
}

export function isBuiltInSuperAdminUser(row: Pick<UserRecord, 'username'>): boolean {
  return row.username === BUILT_IN_SUPER_ADMIN_ACCOUNT
}

/** 列表/详情展示：与 SysUser.userMode / 后端 UserModeEnum 一致 */
export function formatUserModeLabel(mode: number | undefined | null): string {
  if (mode == null) return '—'
  if (mode === REGISTER_TYPE_BACKOFFICE) return '后台用户'
  if (mode === REGISTER_TYPE_PERSONAL) return 'C端个人'
  if (mode === REGISTER_TYPE_ENTERPRISE) return 'C端企业'
  return `其他(${mode})`
}

/** 仅后台用户（userMode=100）展示登录账号；C 端账号（如手机号#200）不展示 */
export function formatUserListAccount(
  row: Pick<UserRecord, 'username' | 'userMode' | 'phone'>,
): string {
  if (row.userMode !== REGISTER_TYPE_BACKOFFICE) return ''
  return String(row.username ?? '').trim()
}

export interface UserPageParams {
  page: number
  pageSize: number
  keyword?: string
  /** 按角色 id 筛选（对应后端 roleIdList） */
  roleId?: string
  /** 按部门/组织筛选（含下级），对应后端 orgId；与 unassignedOrgOnly 互斥 */
  orgId?: string
  /**
   * 与 orgId 互斥。左侧「全局」时：true=仅未绑定部门，false=全部用户（均仅超管）；选部门时不要传
   */
  unassignedOrgOnly?: boolean
  /** 有 orgId 时是否包含下级组织用户；默认 true */
  includeSubOrg?: boolean
  status?: UserStatus
}

export interface UserPageResult {
  list: UserRecord[]
  total: number
}

export interface UserStats {
  totalUsers: number
  enabledUsers: number
  disabledUsers: number
}

export interface CreateUserPayload {
  username: string
  realName: string
  phone: string
  /** SM2 加密后的密文（十六进制） */
  password: string
  deptId: string
  registerType?: number
  roleId?: string[]
}

export interface UpdateUserPayload {
  id: string
  account: string
  name: string
  phone?: string
  orgId: string
  registerType: number
  roleId?: string[]
  password?: string
}

interface SpringPage<T> {
  records?: T[]
  total?: number
}

interface OverseasUserListItem {
  id?: string
  account?: string
  name?: string
  roleName?: string
  userMode?: number
  orgId?: string
  orgName?: string
  phone?: string
  userStatus?: string
  lastLoginTime?: string
}

interface OverseasUserDetail {
  id?: string
  account?: string
  name?: string
  phone?: string
  phoneEnc?: string
  orgId?: string
  orgName?: string
  roleName?: string
  email?: string
  userStatus?: string
  lastLoginTime?: string
  createTime?: string | number
  userMode?: number
  registerType?: number
  roleId?: string[]
  openId?: string
  wxBound?: boolean
  hasPhone?: boolean
  roleIntegrityStatus?: string
  roleIntegrityStatusName?: string
  menuIntegrityStatus?: string
  menuIntegrityStatusName?: string
  confIntegrityStatus?: string
  confIntegrityStatusName?: string
}

function mapUserStatus(raw: string | undefined): UserStatus {
  return raw === 'DISABLED' ? 'disabled' : 'enabled'
}

function formatDateTime(v: unknown): string {
  if (v == null || v === '') return '—'
  if (typeof v === 'number' && Number.isFinite(v)) {
    const d = new Date(v)
    if (!Number.isNaN(d.getTime())) {
      const pad = (n: number) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
    }
  }
  const s = String(v)
  if (s.length >= 16) return s.slice(0, 16).replace('T', ' ')
  return s || '—'
}

function mapListItem(row: OverseasUserListItem): UserRecord | null {
  const id = row.id
  const account = row.account
  const name = row.name
  if (!id || !account || !name) return null
  return {
    id,
    username: account,
    realName: name,
    phone: row.phone ?? '',
    email: '',
    role: row.roleName ?? '',
    userMode: row.userMode,
    department: row.orgName ?? row.orgId ?? '',
    deptId: row.orgId,
    status: mapUserStatus(row.userStatus),
    createdAt: '',
    lastLoginAt: formatDateTime(row.lastLoginTime as string | undefined),
  }
}

function buildUserListFilterQuery(params: UserPageParams, withPagination: boolean): string {
  const q = new URLSearchParams()
  if (withPagination) {
    q.set('current', String(params.page))
    q.set('size', String(params.pageSize))
  }
  const kw = params.keyword?.trim()
  if (kw) q.set('searchKey', kw)
  if (params.status === 'enabled') q.set('userStatus', 'ENABLE')
  if (params.status === 'disabled') q.set('userStatus', 'DISABLED')
  const rid = params.roleId?.trim()
  if (rid) q.append('roleIdList', rid)
  const oid = params.orgId?.trim()
  if (oid) {
    q.set('orgId', oid)
    if (params.includeSubOrg === false) q.set('includeSubOrg', 'false')
  } else if (params.unassignedOrgOnly !== undefined) {
    q.set('unassignedOrgOnly', params.unassignedOrgOnly ? 'true' : 'false')
  }
  return q.toString()
}

function buildUserPageQuery(params: UserPageParams): string {
  return buildUserListFilterQuery(params, true)
}

export async function fetchUserStats(): Promise<UserStats> {
  return { totalUsers: 0, enabledUsers: 0, disabledUsers: 0 }
}

export async function fetchUserPage(params: UserPageParams): Promise<UserPageResult> {
  const qs = buildUserPageQuery(params)
  const raw = await http.get<SpringPage<OverseasUserListItem>>(`${BIZ_PREFIX}/user/page?${qs}`)
  const records = Array.isArray(raw.records) ? raw.records : []
  const list = records.map(mapListItem).filter((x): x is UserRecord => x != null)
  const total = typeof raw.total === 'number' ? raw.total : list.length
  return { list, total }
}

export async function exportUserData(params: UserPageParams): Promise<void> {
  const qs = buildUserListFilterQuery(params, false)
  const url = qs ? `${BIZ_PREFIX}/user/export?${qs}` : `${BIZ_PREFIX}/user/export`
  await download(url, '用户导出.xlsx')
}

function mapDetailToUserRecord(d: OverseasUserDetail): UserRecord | null {
  const id = d.id
  const account = d.account
  const name = d.name
  if (!id || !account || !name) return null
  return {
    id,
    username: account,
    realName: name,
    phone: d.phone ?? '',
    phoneEnc: d.phoneEnc ?? '',
    hasPhone: d.hasPhone === true || Boolean(d.phone?.trim()) || Boolean(d.phoneEnc?.trim()),
    email: d.email ?? '',
    role: d.roleName ?? '',
    department: d.orgName ?? d.orgId ?? '',
    deptId: d.orgId,
    status: mapUserStatus(d.userStatus),
    createdAt: formatDateTime(d.createTime),
    lastLoginAt: formatDateTime(d.lastLoginTime as string | undefined),
    registerType: d.registerType,
    roleId: Array.isArray(d.roleId) ? d.roleId : [],
    userMode: typeof d.userMode === 'number' ? d.userMode : undefined,
    openId: d.openId?.trim() || undefined,
    wxBound: typeof d.wxBound === 'boolean' ? d.wxBound : Boolean(d.openId?.trim()),
    roleIntegrityStatus: d.roleIntegrityStatus,
    roleIntegrityStatusName: d.roleIntegrityStatusName,
    menuIntegrityStatus: d.menuIntegrityStatus,
    menuIntegrityStatusName: d.menuIntegrityStatusName,
    confIntegrityStatus: d.confIntegrityStatus,
    confIntegrityStatusName: d.confIntegrityStatusName,
  }
}

export async function fetchUserDetail(id: string): Promise<UserRecord | null> {
  const raw = await http.get<OverseasUserDetail>(`${BIZ_PREFIX}/user/detail`, {
    params: { id },
  })
  return mapDetailToUserRecord(raw)
}

/** 详情点「解密查看」或编辑回填明文：解业务列 phone（本地 SM4），不走 phone_enc */
export async function fetchUserPhonePlain(id: string): Promise<string> {
  const raw = await http.get<{ id?: string; phone?: string }>(`${BIZ_PREFIX}/user/phonePlain`, {
    params: { id },
  })
  return String(raw?.phone ?? '').trim()
}

export async function createUser(payload: CreateUserPayload): Promise<void> {
  await http.post<void>(`${BIZ_PREFIX}/user/add`, {
    account: payload.username.trim(),
    name: payload.realName.trim(),
    phone: payload.phone.trim(),
    password: payload.password,
    orgId: payload.deptId,
    registerType: payload.registerType ?? REGISTER_TYPE_BACKOFFICE,
    roleId: payload.roleId ?? [],
  })
}

export async function updateUser(_id: string, payload: UpdateUserPayload): Promise<void> {
  await http.post<void>(`${BIZ_PREFIX}/user/edit`, {
    id: payload.id,
    account: payload.account.trim(),
    name: payload.name.trim(),
    phone: payload.phone?.trim(),
    orgId: payload.orgId,
    registerType: payload.registerType,
    roleId: payload.roleId ?? [],
    ...(payload.password ? { password: payload.password } : {}),
  })
}

export async function deleteUser(id: string): Promise<void> {
  await http.post<void>(`${BIZ_PREFIX}/user/delete`, [{ id }])
}

const USER_MANAGE = `${BIZ_PREFIX}/user/manage`

export async function resetUserPassword(id: string): Promise<void> {
  await http.post<void>(`${USER_MANAGE}/resetPassword`, { id })
}

/** 锁定（停用） */
export async function lockUser(id: string): Promise<void> {
  await http.post<void>(`${USER_MANAGE}/lockUser`, { id })
}

/** 解锁（启用） */
export async function unlockUser(id: string): Promise<void> {
  await http.post<void>(`${USER_MANAGE}/unlockUser`, { id })
}

export async function fetchUserOwnRoleIds(id: string): Promise<string[]> {
  const raw = await http.get<unknown>(`${SYS_USER_PREFIX}/ownRole`, { params: { id } })
  return Array.isArray(raw) ? (raw as string[]) : []
}

export interface RoleLite {
  id: string
  name: string
}

/** 参考 yj-admin-ui：用于“已选择”区域回显角色中文名 */
export async function fetchRoleListByIdList(idList: string[]): Promise<RoleLite[]> {
  const ids = idList.map((x) => String(x).trim()).filter(Boolean)
  if (!ids.length) return []
  const raw = await http.post<unknown>(`${SYS_USER_CENTER_PREFIX}/getRoleListByIdList`, {
    idList: ids,
  })
  if (!Array.isArray(raw)) return []
  return raw
    .map((x) => {
      if (!x || typeof x !== 'object') return null
      const o = x as Record<string, unknown>
      const idRaw = o.id ?? o.roleId
      const id = idRaw != null && idRaw !== '' ? String(idRaw) : ''
      const nameRaw = o.name ?? o.roleName
      const name = nameRaw != null && nameRaw !== '' ? String(nameRaw) : ''
      if (!id || !name) return null
      return { id, name }
    })
    .filter((x): x is RoleLite => x != null)
}

export async function grantUserRoles(userId: string, roleIdList: string[]): Promise<void> {
  await http.post<void>(`${SYS_USER_PREFIX}/grantRole`, { id: userId, roleIdList })
}

export interface UserGrantResourceItem {
  menuId: string
  buttonInfo: string[]
}

export async function fetchUserOwnResource(userId: string): Promise<UserGrantResourceItem[]> {
  const raw = await http.get<Record<string, unknown>>(`${USER_MANAGE}/ownResource`, {
    params: { id: userId },
  })
  const list = raw?.grantInfoList
  if (!Array.isArray(list)) return []
  return list
    .map((x) => {
      if (!x || typeof x !== 'object') return null
      const o = x as Record<string, unknown>
      const menuId = o.menuId != null ? String(o.menuId) : ''
      const bi = o.buttonInfo
      const buttonInfo = Array.isArray(bi) ? bi.map((b) => String(b)) : []
      if (!menuId) return null
      return { menuId, buttonInfo }
    })
    .filter((x): x is UserGrantResourceItem => x != null)
}

export async function grantUserResource(
  userId: string,
  grantInfoList: UserGrantResourceItem[],
): Promise<void> {
  await http.post<void>(`${USER_MANAGE}/grantResource`, {
    id: userId,
    grantInfoList,
  })
}

export async function fetchMenuTreeForGrant(module?: string): Promise<unknown> {
  return http.get<unknown>(`${USER_MANAGE}/menuTreeForGrant`, {
    params: module?.trim() ? { module: module.trim() } : {},
  })
}

/** 与后端 SysRoleDataScopeCategoryEnum 一致 */
export const DATA_SCOPE_OPTIONS = [
  { value: 'SCOPE_ALL', label: '全部' },
  { value: 'SCOPE_SELF', label: '仅自己' },
  { value: 'SCOPE_ORG', label: '本机构' },
  { value: 'SCOPE_ORG_CHILD', label: '本机构及以下' },
  { value: 'SCOPE_ORG_DEFINE', label: '自定义机构' },
  { value: 'SCOPE_REGION', label: '区划精确' },
  { value: 'SCOPE_REGION_CHILD', label: '区划前缀' },
] as const

/** 解析 `/path[说明]` */
export function parsePermissionApiLine(line: string): { apiUrl: string; title: string } {
  const s = line.trim()
  const i = s.lastIndexOf('[')
  if (i > 0 && s.endsWith(']')) {
    return { apiUrl: s.slice(0, i).trim(), title: s.slice(i + 1, -1).trim() }
  }
  return { apiUrl: s, title: '' }
}

export async function fetchPermissionApiList(): Promise<string[]> {
  const raw = await http.get<unknown>(`${USER_MANAGE}/permissionApiList`)
  const lines = Array.isArray(raw) ? (raw as string[]) : []
  return lines.filter((line) => typeof line === 'string' && line.trim())
}

export interface DataScopeRuleConfig {
  allowedScopeCategories?: string[]
  allAllowed?: boolean
}

export async function fetchDataScopeRuleMap(
  apiUrls: string[],
): Promise<Record<string, DataScopeRuleConfig>> {
  if (!apiUrls.length) return {}
  const raw = await http.post<Record<string, unknown>>(`${USER_MANAGE}/dataScopeRuleMap`, {
    apiUrls,
  })
  const out: Record<string, DataScopeRuleConfig> = {}
  if (raw && typeof raw === 'object') {
    for (const [k, v] of Object.entries(raw)) {
      if (!v || typeof v !== 'object') continue
      const o = v as Record<string, unknown>
      const allowed = o.allowedScopeCategories
      out[k] = {
        allAllowed: o.allAllowed === true,
        allowedScopeCategories: Array.isArray(allowed) ? allowed.map((x) => String(x)) : undefined,
      }
    }
  }
  return out
}

export interface UserOwnPermissionRow {
  apiUrl: string
  scopeCategory: string
  scopeDefineOrgIdList: string[]
}

export async function fetchUserOwnPermission(userId: string): Promise<UserOwnPermissionRow[]> {
  const raw = await http.get<Record<string, unknown>>(`${USER_MANAGE}/ownPermission`, {
    params: { id: userId },
  })
  const list = raw?.grantInfoList
  if (!Array.isArray(list)) return []
  return list
    .map((x) => {
      if (!x || typeof x !== 'object') return null
      const o = x as Record<string, unknown>
      const apiUrl = parsePermissionApiLine(o.apiUrl != null ? String(o.apiUrl) : '').apiUrl
      const scopeCategory = o.scopeCategory != null ? String(o.scopeCategory) : 'SCOPE_ALL'
      const org = o.scopeDefineOrgIdList
      const scopeDefineOrgIdList = Array.isArray(org) ? org.map((id) => String(id)) : []
      if (!apiUrl) return null
      return { apiUrl, scopeCategory, scopeDefineOrgIdList }
    })
    .filter((x): x is UserOwnPermissionRow => x != null)
}

export async function grantUserPermission(
  userId: string,
  grantInfoList: UserOwnPermissionRow[],
): Promise<void> {
  await http.post<void>(`${USER_MANAGE}/grantPermission`, {
    id: userId,
    grantInfoList,
  })
}

export interface RoleSelectorRecord {
  id: string
  name: string
  /** 后端 GLOBAL / ORG */
  category?: string
}

/** 选组织节点传 orgId；选「全局」可传 category=GLOBAL（后端会列出全局+机构角色） */
export async function fetchRoleSelectorPage(params: {
  current: number
  size: number
  searchKey?: string
  excludeSuperAdmin?: boolean
  category?: string
  orgId?: string
}): Promise<{ records: RoleSelectorRecord[]; total: number }> {
  const q: Record<string, string | number | boolean> = {
    current: params.current,
    size: params.size,
    excludeSuperAdmin: params.excludeSuperAdmin !== false,
  }
  const sk = params.searchKey?.trim()
  if (sk) q.searchKey = sk
  const cid = params.category?.trim()
  if (cid) q.category = cid
  const oid = params.orgId?.trim()
  if (oid) q.orgId = oid
  const raw = await http.get<{ records?: unknown[]; total?: number }>(
    `${SYS_USER_PREFIX}/roleSelector`,
    {
      params: q,
    },
  )
  const records = Array.isArray(raw.records) ? raw.records : []
  const list = records
    .map((r, i) => {
      if (!r || typeof r !== 'object') return null
      const o = r as Record<string, unknown>
      const id = o.id != null ? String(o.id) : `row-${i}`
      const name = o.name != null ? String(o.name) : ''
      if (!name) return null
      const rec: RoleSelectorRecord = { id, name }
      if (o.category != null) rec.category = String(o.category)
      return rec
    })
    .filter((x): x is RoleSelectorRecord => x != null)
  const total = typeof raw.total === 'number' ? raw.total : list.length
  return { records: list, total }
}

export async function tryUploadUsersBatchFile(
  file: File,
  orgId?: string | null,
): Promise<number | null> {
  const fd = new FormData()
  fd.append('file', file)
  const oid = orgId?.trim()
  if (oid) fd.append('orgId', oid)
  const raw = await http.upload<Record<string, unknown>>(`${BIZ_PREFIX}/user/import`, fd)
  if (raw && typeof raw === 'object') {
    const sc = raw.successCount
    const ec = raw.errorCount
    if (typeof ec === 'number' && ec > 0 && sc === 0) {
      const detail = raw.errorDetail
      let first = '导入失败'
      if (Array.isArray(detail) && detail.length > 0) {
        const row = detail[0] as Record<string, unknown>
        const m = row?.msg
        if (typeof m === 'string' && m.trim()) first = m.trim()
      }
      throw new Error(first)
    }
    if (typeof sc === 'number' && Number.isFinite(sc)) return sc
  }
  return null
}

/**
 * 用户列表「角色」筛选项：超管全量；非超管仅当前可见组织下的组织角色
 */
export async function fetchUserRoleFilterOptions(): Promise<{ id: string; name: string }[]> {
  const raw = await http.get<unknown>(`${BIZ_PREFIX}/role/userFilterOptions`)
  if (!Array.isArray(raw)) return []
  return raw
    .map((x) => {
      if (!x || typeof x !== 'object') return null
      const o = x as Record<string, unknown>
      const idRaw = o.id ?? o.roleId
      const id = idRaw != null && idRaw !== '' ? String(idRaw) : ''
      const nameRaw = o.name ?? o.roleName
      const name = nameRaw != null && nameRaw !== '' ? String(nameRaw) : ''
      if (!id || !name) return null
      return { id, name }
    })
    .filter((x): x is { id: string; name: string } => x != null)
}
